import type { ParsedTradovateTrade } from "./tradovateCSVParser";

/**
 * Filters parsed (pasted/CSV) trades down to those that will pass the server's
 * `importTradesFromCSVInputSchema` Zod validation. A single trade that violates
 * a `z.number().positive()` / `z.coerce.date()` / `symbol.min(1).max(20)` rule
 * makes the whole `importTradesFromCSV` action throw a 500, which Wasp Router
 * surfaces as the blank "error rendering this page" screen. Dropping the bad
 * rows client-side (and telling the user how many were skipped) keeps the import
 * resilient instead of letting one malformed row blank the page.
 *
 * Mirror of the constraints in trading-journal/operations.ts
 * (importTradesFromCSVInputSchema). Keep the two in sync.
 */
export function sanitizeParsedTrades(
  trades: ParsedTradovateTrade[],
): { valid: ParsedTradovateTrade[]; skipped: number } {
  const valid: ParsedTradovateTrade[] = [];
  let skipped = 0;

  const isValidDate = (d: unknown): d is Date =>
    d instanceof Date && !Number.isNaN(d.getTime());

  for (const t of trades) {
    const symbolOk =
      typeof t.symbol === "string" && t.symbol.length >= 1 && t.symbol.length <= 20;
    const entryOk = Number.isFinite(t.entryPrice) && t.entryPrice > 0;
    const exitOk = Number.isFinite(t.exitPrice) && t.exitPrice > 0;
    const qtyOk = Number.isFinite(t.qty) && t.qty > 0;
    const pnlOk = Number.isFinite(t.pnl);
    const datesOk = isValidDate(t.entryTime) && isValidDate(t.exitTime);

    if (symbolOk && entryOk && exitOk && qtyOk && pnlOk && datesOk) {
      valid.push(t);
    } else {
      skipped++;
    }
  }

  return { valid, skipped };
}
